import { motion } from 'framer-motion'
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import WebDevelopmentIcon from '../components/icons/WebDevelopmentIcon'
import CloudIcon from '../components/icons/CloudIcon'
import ConsultingIcon from '../components/icons/ConsultingIcon'
import SupportIcon from '../components/icons/SupportIcon'
import AIIcon from '../components/icons/AIIcon'
import Web3Icon from '../components/icons/Web3Icon'
import WalkingIcon from '../components/icons/WalkingIcon'
import MaleAvatarIcon from '../components/icons/MaleAvatarIcon'
import CrmIcon from '../components/icons/CrmIcon'
import DevelopmentIcon from '../components/icons/DevelopmentIcon'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateDubaiLocalBusinessSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'
import ServicesHeroIcons from '../components/ServicesHeroIcons'

interface Service {
  id: string
  title: string
  shortDesc: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  features: string[]
  technologies: string[]
}

const Services = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeService, setActiveService] = useState(0)
  const serviceDetailsRef = useRef<HTMLDivElement>(null)

  const services: Service[] = useMemo(() => [
    {
      id: 'development',
      title: 'Development',
      shortDesc: 'Modern web & mobile applications',
      description: 'We build scalable, performant applications using cutting-edge technologies. From responsive websites to complex enterprise systems, we deliver solutions that drive your business forward.',
      icon: WebDevelopmentIcon,
      features: [
        'Custom Web Applications',
        'Progressive Web Apps',
        'API Development',
        'Database Design',
        'Performance Optimization',
        'Responsive Design'
      ],
      technologies: ['React', 'Node.js', 'TypeScript', 'PostgreSQL', 'AWS', 'Docker']
    },
    {
      id: 'cloud',
      title: 'Cloud Solutions',
      shortDesc: 'Scalable infrastructure & DevOps',
      description: 'Transform your infrastructure with cloud-native solutions. We help you migrate, optimize, and manage your cloud environment for maximum efficiency and reliability.',
      icon: CloudIcon,
      features: [
        'Cloud Migration',
        'Infrastructure as Code',
        'CI/CD Pipelines',
        'Kubernetes Orchestration',
        'Monitoring & Logging',
        'Cost Optimization'
      ],
      technologies: ['AWS', 'Azure', 'Terraform', 'Kubernetes', 'Jenkins', 'Prometheus']
    },
    {
      id: 'consulting',
      title: 'IT Consulting',
      shortDesc: 'Strategic technology guidance',
      description: 'Navigate digital transformation with expert guidance. We align technology with your business goals, helping you make informed decisions and implement effective solutions.',
      icon: ConsultingIcon,
      features: [
        'Technology Roadmaps',
        'Architecture Reviews',
        'Security Assessments',
        'Process Optimization',
        'Vendor Selection',
        'Team Training'
      ],
      technologies: ['TOGAF', 'Agile', 'ITIL', 'ISO 27001', 'GDPR', 'Best Practices']
    },
    {
      id: 'support',
      title: 'Support & Maintenance',
      shortDesc: '24/7 technical assistance',
      description: 'Keep your systems running smoothly with proactive maintenance and rapid support. We ensure your technology infrastructure remains secure, updated, and optimized.',
      icon: SupportIcon,
      features: [
        '24/7 Monitoring',
        'Security Updates',
        'Performance Tuning',
        'Backup Management',
        'Incident Response',
        'Documentation'
      ],
      technologies: ['Datadog', 'PagerDuty', 'Grafana', 'ELK Stack', 'Ansible', 'Git']
    },
    {
      id: 'ai',
      title: 'AI',
      shortDesc: 'Intelligent automation & insights',
      description: 'Harness the power of artificial intelligence to transform your business. We implement cutting-edge AI solutions that automate processes, enhance decision-making, and unlock new opportunities for growth.',
      icon: AIIcon,
      features: [
        'AI Strategy & Consulting',
        'Custom AI Model Development',
        'Natural Language Processing',
        'Computer Vision Solutions',
        'Predictive Analytics',
        'AI Integration Services'
      ],
      technologies: ['OpenAI', 'TensorFlow', 'PyTorch', 'LangChain', 'Hugging Face', 'Azure AI', 'ElizaOS']
    },
    {
      id: 'web3',
      title: 'Web3 & Blockchain',
      shortDesc: 'Decentralized solutions & smart contracts',
      description: 'Build the future of the internet with blockchain technology. We develop secure, scalable Web3 applications, smart contracts, and decentralized solutions that empower your business in the digital economy.',
      icon: Web3Icon,
      features: [
        'Smart Contract Development',
        'DApp Development',
        'Blockchain Integration',
        'NFT Marketplace Solutions',
        'DeFi Applications',
        'Web3 Wallet Integration',
        'Node Maintenance'
      ],
      technologies: ['Ethereum', 'Solidity', 'Web3.js', 'IPFS', 'Polygon', 'Hardhat']
    }
  ], [])

  // Handle URL parameter to set active service
  useEffect(() => {
    const serviceParam = searchParams.get('service')
    if (serviceParam) {
      const serviceIndex = services.findIndex(s => s.id === serviceParam)
      if (serviceIndex !== -1) {
        setActiveService(serviceIndex)
        // Auto-scroll to service details when navigating from homepage or direct link
        setTimeout(() => {
          serviceDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 300)
      }
    }
  }, [searchParams, services])

  // Handle service selection - update both state and URL
  const handleServiceSelect = (index: number) => {
    setActiveService(index)
    setSearchParams({ service: services[index].id })
    // Smooth scroll to service details
    setTimeout(() => {
      serviceDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: service.title,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE',
    offers: {
      price: 'Contact for pricing',
      priceCurrency: 'AED'
    }
  }));

  const dubaiLocalBusinessSchema = generateDubaiLocalBusinessSchema();
  const structuredData = [...serviceSchemas, dubaiLocalBusinessSchema];

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Software Development Dubai | Cloud Solutions & IT Services UAE"
        description="Professional software development Dubai and cloud solutions for UAE businesses. Web development, AI integration, blockchain development, and IT consulting services to transform your business."
        keywords={['IT services Dubai', 'web development Dubai UAE', 'cloud solutions Dubai', 'AI integration Dubai', 'blockchain development UAE', 'technical consulting Dubai', 'DevOps services Dubai', 'digital transformation Dubai', 'software development Dubai', 'cloud services UAE', 'Web3 development Dubai']}
        url={currentUrl}
        image="https://demirix.com/services-hero.jpg"
        structuredData={structuredData}
      />
      <Breadcrumbs />
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <ServicesHeroIcons />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Software Development & Cloud Solutions in Dubai
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              From software development to cloud solutions deployment, we provide comprehensive technology services
              that help Dubai businesses thrive in the digital age. Learn more <a href="/about" className="text-primary-600 dark:text-primary-400 hover:underline font-semibold">about our approach</a> to technology consulting.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Selector Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {/* Service Tabs - Mobile Grid */}
            <div className="lg:hidden pb-4 mb-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {services.map((service, index) => {
                  const Icon = service.icon
                  return (
                    <motion.button
                      key={service.id}
                      variants={itemVariants}
                      onClick={() => handleServiceSelect(index)}
                      className={`
                        p-3 rounded-lg border-2 transition-all duration-300
                        ${activeService === index
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-500 shadow-lg scale-105'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-300'
                        }
                      `}
                    >
                      <Icon className="w-8 h-8 mx-auto mb-1" />
                      <h3 className={`font-semibold text-xs ${activeService === index ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                        {service.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden">
                        {service.shortDesc}
                      </p>
                    </motion.button>
                  )
                })}
              </div>
            </div>

            {/* Service Tabs - Desktop Grid */}
            <div className="hidden lg:grid grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto">
              {services.map((service, index) => {
                const Icon = service.icon
                return (
                  <motion.button
                    key={service.id}
                    variants={itemVariants}
                    onClick={() => handleServiceSelect(index)}
                    className={`
                      p-4 rounded-lg border-2 transition-all duration-300
                      ${activeService === index
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-500 shadow-lg scale-105'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:scale-105'
                      }
                    `}
                  >
                    <Icon className="w-8 h-8 mx-auto mb-2" />
                    <h3 className={`font-semibold text-sm ${activeService === index ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                      {service.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {service.shortDesc}
                    </p>
                  </motion.button>
                )
              })}
            </div>

            {/* Active Service Details */}
            <motion.div
              ref={serviceDetailsRef}
              key={activeService}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 sm:p-8 lg:p-12 scroll-mt-24"
            >
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="text-center lg:text-left">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    {services[activeService].title}
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                    {services[activeService].description}
                  </p>
                  
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    What we offer:
                  </h3>
                  <ul className="space-y-3 mb-8 text-left max-w-md mx-auto lg:mx-0">
                    {services[activeService].features.map((feature, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start"
                      >
                        <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <div className="flex justify-center lg:justify-start">
                    <CalendlyButton 
                      className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105"
                    >
                      Discuss Your Project
                    </CalendlyButton>
                  </div>
                </div>

                <div>
                  <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Technologies we use:
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {services[activeService].technologies.map((tech: string, index: number) => (
                        <motion.span
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.05 }}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium"
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>
                  </div>

                  {/* Visual Element */}
                  <div className="mt-8 relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 rounded-2xl blur-3xl opacity-50" />
                    <div className="relative bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-8 text-white text-center">
                      <div className="text-5xl font-bold mb-2">100%</div>
                      <div className="text-lg">Client Satisfaction</div>
                      <div className="text-sm opacity-80 mt-2">Based on project delivery and support quality</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How We Work
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Our proven process ensures successful project delivery and lasting partnerships
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="flex flex-col lg:grid lg:grid-cols-4 lg:gap-16"
          >
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We understand your business needs and technical requirements'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'We design a tailored solution and create a detailed roadmap'
              },
              {
                step: '03',
                title: 'Execution',
                description: 'We build and deploy your solution with agile methodology'
              },
              {
                step: '04',
                title: 'Support',
                description: 'We provide ongoing maintenance and continuous improvement'
              }
            ].map((phase, index) => (
              <React.Fragment key={`phase-${index}`}>
                <motion.div
                  variants={itemVariants}
                  className="relative"
                >
                  <div className="bg-white dark:bg-gray-700 rounded-xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300 border border-gray-100 dark:border-gray-600 text-center lg:text-left">
                    <div className="text-6xl sm:text-7xl font-black bg-gradient-to-br from-primary-400 to-primary-600 bg-clip-text text-transparent mb-6">
                      {phase.step}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      {phase.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                      {phase.description}
                    </p>
                  </div>
                  {index < 3 && (
                    /* Desktop walking icon - positioned on the right */
                    <motion.div 
                      className="hidden lg:block absolute top-1/2 -right-12 transform -translate-y-1/2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8 + index * 0.4, duration: 0.6 }}
                    >
                      <WalkingIcon className="w-10 h-10" direction="right" />
                    </motion.div>
                  )}
                </motion.div>
                
                {/* Mobile walking icon - outside card */}
                {index < 3 && (
                  <motion.div 
                    className="lg:hidden flex justify-center py-4"
                    initial={{ opacity: 0, y: -10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <WalkingIcon className="w-10 h-10" direction="down" />
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Dubai Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-2xl p-8 lg:p-12 border border-primary-200 dark:border-primary-700"
            >
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-block px-3 py-1 bg-primary-600 text-white text-sm font-medium rounded-full mb-4">
                    Dubai, UAE
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                    Specialized Services for Dubai Businesses
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                    Looking for a technology partner in Dubai? We offer tailored IT services for the UAE market,
                    including Arabic language support, local payment integration, and compliance with UAE regulations.
                  </p>
                  <Link
                    to="/dubai-services"
                    className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105"
                  >
                    Explore Dubai Services
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'GST Timezone', value: 'Aligned' },
                    { label: 'Arabic Support', value: 'RTL Ready' },
                    { label: 'UAE Compliance', value: 'Certified' },
                    { label: 'Local Payments', value: 'Integrated' }
                  ].map((item, index) => (
                    <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center shadow-sm">
                      <div className="text-xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                        {item.value}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Related Insights Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Related Insights
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Explore our latest articles on technology trends and best practices
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Digital Transformation in UAE',
                  description: 'A comprehensive guide to navigating your digital journey in the UAE market.',
                  path: '/blog/digital-transformation-uae',
                  Icon: MaleAvatarIcon
                },
                {
                  title: 'Enterprise Software Solutions for Dubai',
                  description: 'Learn about ERP, CRM, and custom software solutions for UAE businesses.',
                  path: '/blog/enterprise-software-dubai',
                  Icon: CrmIcon
                },
                {
                  title: 'Software Development Trends 2026',
                  description: 'Discover the leading technology trends shaping Dubai\'s tech landscape.',
                  path: '/blog/dubai-software-trends-2026',
                  Icon: DevelopmentIcon
                }
              ].map((post, index) => (
                <motion.div key={index} variants={itemVariants}>
                  <Link
                    to={post.path}
                    className="group block bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 h-full"
                  >
                    {/*
                      Decorative, so aria-hidden and no role. It previously carried both
                      role="img" and aria-hidden, which contradict each other: the role
                      announces an image while aria-hidden removes it from the tree. The
                      card's own heading and description carry the meaning.
                    */}
                    <div className="w-12 h-12 mb-4" aria-hidden="true">
                      <post.Icon className="w-full h-full" darkTreatment="none" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                      {post.description}
                    </p>
                    <span className="inline-flex items-center text-primary-600 dark:text-primary-400 text-sm font-medium group-hover:underline">
                      Read more
                      <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Transform Your Business?"
        description="Let's discuss how our services can help you achieve your technology goals and drive business growth."
        primary={{ kind: 'calendly', label: 'Schedule a Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact', asAnchor: true }}
        size="lg"
      />
    </div>
  )
}

export default Services